/**
 * Announcements-only panel — full-screen rotating cards.
 *
 * Parity with apps/signage-tv/lib/rendering/announcements_only_panel.dart.
 * Single class; owns a 100ms ticker for dwell + progress-bar animation;
 * crossfades cards via the browser's CSS opacity transition. Falls back
 * to the idle panel when the list is empty (spec §3.4).
 */

import { applyBranding, Branding, extractBranding } from './branding.js';
import { AnnouncementRotator } from './announcement-rotator.js';
import { renderIdle } from './idle.js';
import { renderAnnouncementCard } from '../primitives/announcement-card.js';

export interface AnnouncementsOnlyState {
  config: Record<string, unknown> | null;
  orgContent: Record<string, unknown> | null;
}

const TICK_MS = 100;
const CROSSFADE_MS = 600;

export class AnnouncementsOnlyRenderer {
  private readonly rotator = new AnnouncementRotator();
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private lastRenderedId: string | null = null;
  private currentState: AnnouncementsOnlyState = { config: null, orgContent: null };
  private branding: Branding;

  constructor(private readonly root: HTMLElement) {
    this.branding = extractBranding(null);
  }

  dispose(): void {
    if (this.tickTimer !== null) clearInterval(this.tickTimer);
    this.tickTimer = null;
    this.lastRenderedId = null;
  }

  render(state: AnnouncementsOnlyState): void {
    this.currentState = state;
    this.branding = extractBranding(state.config);
    applyBranding(this.root, this.branding);

    const list = this.announcements();
    if (list.length === 0) {
      this.dispose();
      this.root.innerHTML = '';
      renderIdle(this.root, state.config, state.orgContent, 0);
      return;
    }

    // Initial paint — build the scaffold (header, stage, progress strip).
    if (
      !this.root.classList.contains('ag-announcements') ||
      this.root.childElementCount === 0
    ) {
      this.root.innerHTML = '';
      this.root.className = 'ag-app ag-announcements';

      const header = document.createElement('div');
      header.className = 'ag-announcements__header';
      header.style.borderBottom = `3px solid ${this.branding.primaryColor}`;
      header.textContent = this.branding.orgName;
      this.root.appendChild(header);

      const stage = document.createElement('div');
      stage.className = 'ag-announcements__stage';
      this.root.appendChild(stage);

      const strip = document.createElement('div');
      strip.className = 'ag-announcements__strip';
      const fill = document.createElement('div');
      fill.className = 'ag-announcements__strip-fill';
      fill.style.background = this.branding.primaryColor;
      strip.appendChild(fill);
      this.root.appendChild(strip);
    }

    // Paint the current card (mount or crossfade as needed).
    this.paintCurrentCard(list);

    // Start the tick loop if not already running.
    if (this.tickTimer === null) {
      this.tickTimer = setInterval(() => this.tick(), TICK_MS);
    }
  }

  private tick(): void {
    const list = this.announcements();
    if (list.length === 0) return;
    const now = Date.now();
    const moved = this.rotator.maybeAdvance(list, now);
    if (moved) {
      this.paintCurrentCard(list);
    } else {
      // Just repaint the progress strip.
      this.paintProgress(list, now);
    }
  }

  private announcements(): Record<string, unknown>[] {
    const raw = this.currentState.orgContent?.['announcements'];
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (x): x is Record<string, unknown> =>
        x !== null && typeof x === 'object' && !Array.isArray(x)
    );
  }

  private paintCurrentCard(list: Record<string, unknown>[]): void {
    const stage = this.root.querySelector<HTMLElement>(
      '.ag-announcements__stage'
    );
    if (!stage) return;

    const ann = list[this.rotator.index % list.length] as Record<string, unknown>;
    const id = typeof ann['id'] === 'string' ? (ann['id'] as string) : '';
    if (id === this.lastRenderedId) return; // Nothing to do.

    // Crossfade: fade existing card out, insert new card faded in. CSS
    // does the actual opacity transition; we just set classes and let the
    // browser take care of it.
    const oldCard = stage.querySelector<HTMLElement>('.ag-ann-card');
    const card = this.buildCard(ann);
    card.style.opacity = '0';
    stage.appendChild(card);
    // Next microtask: start fade-in.
    requestAnimationFrame(() => {
      card.style.opacity = '1';
    });
    if (oldCard) {
      oldCard.classList.add('ag-ann-card--exiting');
      setTimeout(() => oldCard.remove(), CROSSFADE_MS + 50);
    }
    this.lastRenderedId = id;
    this.paintProgress(list, Date.now());
  }

  private paintProgress(list: Record<string, unknown>[], nowMs: number): void {
    const fill = this.root.querySelector<HTMLElement>(
      '.ag-announcements__strip-fill'
    );
    if (!fill) return;
    const p = this.rotator.progress(list, nowMs);
    fill.style.width = `${Math.max(0, Math.min(1, p)) * 100}%`;
  }

  private buildCard(ann: Record<string, unknown>): HTMLElement {
    const publishedAt =
      typeof ann['published_at'] === 'string' ? (ann['published_at'] as string) : '';
    return renderAnnouncementCard({
      title: typeof ann['title'] === 'string' ? (ann['title'] as string) : '',
      body: typeof ann['body'] === 'string' ? (ann['body'] as string) : undefined,
      imageUrl: typeof ann['image_url'] === 'string' ? (ann['image_url'] as string) : undefined,
      ctaLabel: typeof ann['cta_label'] === 'string' ? (ann['cta_label'] as string) : undefined,
      ctaUrl: typeof ann['cta_url'] === 'string' ? (ann['cta_url'] as string) : undefined,
      priority: typeof ann['priority'] === 'string' ? (ann['priority'] as string) : 'normal',
      publishedAtDisplay: publishedAt ? formatPublishedAt(publishedAt) : undefined,
    });
  }
}

function formatPublishedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}
